import { getDataPointLabel } from '@modules/common/hooks/useGetBucketLabel/getDataPointLabel';
import { ReportDataPoint } from '@modules/common/types';
import {
  getDataPointId,
  getIdOrLabelFromCategory,
  mapEdgeCategoryToCategory,
  toSentimentName,
} from '@modules/common/utils';
import {
  AggregateBucketKey,
  AggregateDataV2,
  SentimentType,
} from 'generated/graphql';
import { round } from 'lodash';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface getQuantifyTableRowsParams {
  aggregateDataV2List: AggregateDataV2[];
  whitelistIDs?: string[];
  shouldRemoveFileUploadPrefix: boolean;
}

export function getQuantifyTableRows({
  whitelistIDs,
  aggregateDataV2List,
  shouldRemoveFileUploadPrefix = false,
}: getQuantifyTableRowsParams): ReportDataPoint[] {
  const dataPoints = aggregateDataV2List
    ?.map(({ bucket, values }) => {
      if (bucket == null) {
        return undefined;
      }

      const aggregateBucket = bucket as AggregateBucketKey;
      const id = getDataPointId(aggregateBucket);
      const category = mapEdgeCategoryToCategory(aggregateBucket?.category);
      const isInvisible = aggregateBucket.label?.isVisible === false;
      const isUpdateInProgress =
        Boolean(aggregateBucket.label?.updateInProgress) && !isInvisible;

      const isEntitySentimentVariation = values.some(
        (value) =>
          !!value.entitySentiment && !value.timestamp && !value.queryID,
      );
      const variants: ReportDataPoint['variants'] = {};

      if (!!whitelistIDs?.length) {
        const idWithCategory = getIdOrLabelFromCategory({
          key: id,
          category,
        });

        if (
          !(whitelistIDs.includes(id) || whitelistIDs.includes(idWithCategory))
        ) {
          return undefined;
        }
      }

      if (isEntitySentimentVariation) {
        Object.values(SentimentType).forEach((sentimentType) => {
          variants[sentimentType] = {
            id: sentimentType,
            label: toSentimentName(sentimentType),
            value: 0,
          };
        });
      }

      let recentVariantAnomalyValue:
        | {
            zScore: number;
            mean: number;
          }
        | undefined = undefined;

      let isRelevant = true;

      values.forEach((value) => {
        const anomaly = value?.intermediates?.find(
          (intermediate) => intermediate.func === EQLFunctionName.Anomaly,
        )?.value?.anomaly;

        const anomalyValue = anomaly?.isAnomalous
          ? {
              zScore: anomaly.zScore ? round(anomaly.zScore, 2) : 0,
              mean: anomaly.mean ? round(anomaly.mean, 2) : 0,
            }
          : undefined;

        if (anomalyValue) {
          recentVariantAnomalyValue = anomalyValue;
        }

        if (isRelevant === true) {
          isRelevant =
            value?.intermediates?.find(
              (intermediate) =>
                intermediate.func === EQLFunctionName.ApplyTaxonomy,
            )?.value?.applyTaxonomy?.isRelevant ?? true;
        }

        const variant = isEntitySentimentVariation
          ? value.entitySentiment
          : value.timestamp;

        if (!value.queryID && variant) {
          variants[variant] = {
            id: String(variant),
            label: value.entitySentiment
              ? toSentimentName(value.entitySentiment)
              : String(value.timestamp),
            value: round(value.value, 2),
            anomalyValue,
          };
        }
      });

      const value =
        values.find(({ timestamp, entitySentiment }) => {
          return !(timestamp || entitySentiment);
        })?.value ?? 0;

      const dataPoint: ReportDataPoint = {
        id,
        label:
          getDataPointLabel({
            bucket: aggregateBucket,
            shouldRemoveFileUploadPrefix,
          }) ?? '',
        value: round(value, 2),
        timestamp: aggregateBucket.timestamp,
        entitySentiment: aggregateBucket.entitySentiment,
        category,
        variants,
        anomalyValue: recentVariantAnomalyValue,
        isAnomalous: !!recentVariantAnomalyValue,
        isRelevant,
        isInvisible,
        isUpdateInProgress,
      };

      return dataPoint;
    })
    .filter((item): item is ReportDataPoint => !!item);

  return dataPoints;
}
